// src/pages/Stories.js
import React, { useState, useEffect, useMemo } from 'react';
import processSentenceJisho from '../utils/processSentenceJisho';
import ClickableSentence from '../components/ClickableSentence';
import apiClient from '../utils/apiClient';
import logger from '../utils/logger';
import userActivityService from '../services/userActivityService';
import { useFirebase } from '../contexts/FirebaseContext';
import { Link } from 'react-router-dom';
import Pricing from './Pricing';
import '../styles/Stories.css';

const DAILY_STORY_KEY = 'dailyStory';

const getStoryResetDate = () => {
    const now = new Date();
    const thresholdHour = 4;
    const thresholdMinute = 0;
    if (
        now.getHours() < thresholdHour ||
        (now.getHours() === thresholdHour && now.getMinutes() < thresholdMinute)
    ) {
        now.setDate(now.getDate() - 1);
    }
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const Stories = () => {
    const { userPlan, planExpiresAt, isAdmin } = useFirebase();
    const [settings, setSettings] = useState({
        learningLanguage: localStorage.getItem('learningLanguage') || "Spanish",
        languageLevel: localStorage.getItem('languageLevel') || "Beginner",
        dailyStoryRepeatNumber: Number(localStorage.getItem('dailyStoryRepeatNumber')) || 0,
        baseLanguage: localStorage.getItem('baseLanguage') || "English"
    });
    const [storyData, setStoryData] = useState({ title: "", titleTranslation: "", story: [], translation: [] });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('original');
    const [loadingMessage, setLoadingMessage] = useState('Loading Story...');
    const [reloadCount, setReloadCount] = useState(0);
    const [maxReloadReached, setMaxReloadReached] = useState(false);
    const [maxReloadLimit, setMaxReloadLimit] = useState(1);
    const [showDebugInfo, setShowDebugInfo] = useState('disabled');
    const [showPricingOverlay, setShowPricingOverlay] = useState(false);

    // --- "Paste your own text" state ---
    const [pasteText, setPasteText] = useState('');
    const [isPasting, setIsPasting] = useState(false);
    const [isProcessingText, setIsProcessingText] = useState(false);

    const loadingMessages = useMemo(() => [
        "Loading Story...",
        "Just one moment...",
        "Thanks for your patience..."
    ], []);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                logger.debug('Stories - Fetching settings...');
                const response = await apiClient.get('/api/settings');

                if (!response.ok) {
                    throw new Error(`Failed to fetch settings: ${response.status}`);
                }

                const data = await response.json();
                logger.debug('Stories - Settings fetched successfully:', data);

                setSettings({
                    learningLanguage: data.learningLanguage || "Spanish",
                    languageLevel: data.languageLevel || "Beginner",
                    dailyStoryRepeatNumber: data.dailyStoryRepeatNumber || 0,
                    baseLanguage: data.baseLanguage || "English"
                });
            } catch (error) {
                logger.error("Stories - Error fetching settings:", error);

                const storedLanguage = localStorage.getItem('learningLanguage');
                if (storedLanguage) {
                    logger.debug('Stories - Using settings from localStorage as fallback');
                    setSettings({
                        learningLanguage: localStorage.getItem('learningLanguage') || "Spanish",
                        languageLevel: localStorage.getItem('languageLevel') || "Beginner",
                        dailyStoryRepeatNumber: Number(localStorage.getItem('dailyStoryRepeatNumber')) || 0,
                        baseLanguage: localStorage.getItem('baseLanguage') || "English"
                    });
                }
            }
        };
        fetchSettings();
    }, []);

    const { learningLanguage, languageLevel, dailyStoryRepeatNumber, baseLanguage } = settings;

    const getStoryReloadLimit = () => {
        if (isAdmin) return Infinity; // No limit for admins
        switch (userPlan) {
            case 'premium':
                return 10;
            case 'pro':
                return 5;
            case 'trial':
            default:
                return 1;
        }
    };

    useEffect(() => {
        setMaxReloadLimit(getStoryReloadLimit());
    }, [userPlan]);

    useEffect(() => {
        if (!learningLanguage) return;

        const fetchReloadCount = async () => {
            try {
                const reloadCounts = await userActivityService.getReloadCounts();
                const currentCount = reloadCounts.stories[learningLanguage] || 0;
                setReloadCount(currentCount);
                setMaxReloadReached(currentCount >= getStoryReloadLimit());
            } catch (error) {
                logger.error("Stories - Error fetching reload count:", error);
                setReloadCount(0);
            }
        };

        fetchReloadCount();
    }, [learningLanguage, userPlan]);

    useEffect(() => {
        let timer;
        if (loading) {
            let idx = 0;
            setLoadingMessage(loadingMessages[idx]);
            timer = setInterval(() => {
                idx = (idx + 1) % loadingMessages.length;
                setLoadingMessage(loadingMessages[idx]);
            }, 10000);
        } else {
            setLoadingMessage("");
        }
        return () => clearInterval(timer);
    }, [loading, loadingMessages]);

    useEffect(() => {
        if (!learningLanguage) return;

        const fetchStoryFromServer = async () => {
            try {
                const userActivity = await userActivityService.getUserActivity();
                const storedStory = userActivity.stories?.[learningLanguage];

                if (storedStory) {
                    logger.debug('Stories - Using cached story from server');
                    setStoryData(storedStory);
                } else {
                    setStoryData({ title: "", titleTranslation: "", story: [], translation: [] });
                }
            } catch (error) {
                logger.error("Stories - Error fetching story from server:", error);
                setStoryData({ title: "", titleTranslation: "", story: [], translation: [] });
            }
        };

        fetchStoryFromServer();
    }, [languageLevel, learningLanguage]);

    const fetchStory = async () => {
        setLoading(true);
        setLoadingMessage("Loading your story...");
        setError(null);

        try {
            let chineseCharacterType = null;
            if (learningLanguage === 'Chinese') {
                chineseCharacterType = localStorage.getItem('chineseCharacterType') || 'Simplified';
                logger.debug('Stories - Using Chinese character type:', chineseCharacterType);
            }

            logger.debug('Stories - Fetching story with settings:', {
                languageLevel,
                learningLanguage,
                dailyStoryRepeatNumber,
                chineseCharacterType: learningLanguage === 'Chinese' ? chineseCharacterType : 'N/A'
            });

            const data = await apiClient.post('/api/generateStory', {
                languageLevel,
                learningLanguage,
                dailyStoryRepeatNumber: Number(dailyStoryRepeatNumber),
                baseLanguage: baseLanguage,  
                ...(learningLanguage === 'Chinese' && { chineseCharacterType })
            });

            logger.debug('Stories - Received story data:', data);

            if (data.error) {
                logger.error('Stories - Server returned error object:', data.error);
                throw new Error(data.error);
            }

            if (!data.story || !Array.isArray(data.story) || data.story.length === 0) {
                logger.error('Stories - Received invalid story data structure:', data);
                throw new Error('Received invalid story data from server');
            }

            logger.debug('Stories - About to call reloadStory with language:', learningLanguage);
            try {
                const result = await userActivityService.reloadStory(learningLanguage, data);
                logger.debug('Stories - reloadStory result:', result);

                setStoryData(data);
                setIsPasting(false);

                if (result && typeof result.reloadCount === 'number') {
                    setReloadCount(result.reloadCount);

                    if (result.reloadCount >= getStoryReloadLimit()) {
                        setMaxReloadReached(true);
                    }
                }
            } catch (reloadError) {
                logger.error('Stories - Error in reloadStory:', reloadError);
                logger.error('Stories - reloadStory was called with data:', data);
                throw new Error(`Error saving story: ${reloadError.message}`);
            }
        } catch (err) {
            logger.error("Stories - Error fetching story:", err);
            setError("Could not load the daily story. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    /**
     * Sends the text the user pasted to the server, which returns it in the
     * exact same shape as a generated story (title, translation, and sentences
     * that render with every word clickable).
     */
    const handleProcessText = async () => {
        const text = pasteText.trim();
        if (!text) return;

        setIsProcessingText(true);
        setError(null);

        try {
            let chineseCharacterType = null;
            if (learningLanguage === 'Chinese') {
                chineseCharacterType = localStorage.getItem('chineseCharacterType') || 'Simplified';
            }

            const data = await apiClient.post('/api/processText', {
                text,
                learningLanguage,
                languageLevel,
                baseLanguage,
                ...(learningLanguage === 'Chinese' && { chineseCharacterType })
            });

            if (data && data.error) {
                throw new Error(data.error);
            }

            if (!data || !Array.isArray(data.story) || data.story.length === 0) {
                throw new Error('Received invalid processed text from server');
            }

            const processedStory = {
                title: data.title || '',
                titleTranslation: data.titleTranslation || '',
                story: data.story,
                translation: Array.isArray(data.translation) ? data.translation : []
            };

            setStoryData(processedStory);
            setIsPasting(false);
            setActiveTab('original');

            // Persist the pasted story so it survives a page reload. This uses
            // the non-incrementing save endpoint, so it does NOT consume the
            // user's daily story reload allowance.
            try {
                await userActivityService.saveStory(learningLanguage, processedStory);
                logger.debug('Stories - Pasted story saved for persistence');
            } catch (saveError) {
                logger.error('Stories - Error persisting pasted story:', saveError);
            }
        } catch (err) {
            logger.error('Stories - Error processing pasted text:', err);
            setError("Could not process your text. Please try again.");
        } finally {
            setIsProcessingText(false);
        }
    };

    const isTrialExpired = () => {
        if (userPlan === 'canceled') return true;

        if (userPlan !== 'trial' || !planExpiresAt) return false;
        const now = new Date();
        const expiration = new Date(planExpiresAt);
        return now > expiration;
    };

    const getAccessLimitMessage = () => {
        if (userPlan === 'canceled') {
            return (
                <>
                    Your subscription has been canceled. Please <Link
                        onClick={(e) => {
                            e.preventDefault();
                            setShowPricingOverlay(true);
                        }}
                        to="#"
                        style={{ color: '#c41c1c', textDecoration: 'underline', fontWeight: 'bold' }}>
                        upgrade
                    </Link> to regain access.
                </>
            );
        } else if (userPlan === 'trial' && isTrialExpired()) {
            return (
                <>
                    Your trial has ended. Please <Link
                        onClick={(e) => {
                            e.preventDefault();
                            setShowPricingOverlay(true);
                        }}
                        to="#"
                        style={{ color: '#c41c1c', textDecoration: 'underline', fontWeight: 'bold' }}>
                        upgrade
                    </Link> to continue using this feature.
                </>
            );
        } else {
            return userPlan === 'trial' ? (
                <>
                    You have reached your max {getStoryReloadLimit()} story limit for {learningLanguage}. You can <Link
                        onClick={(e) => {
                            e.preventDefault();
                            setShowPricingOverlay(true);
                        }}
                        to="#"
                        style={{ color: '#c41c1c', textDecoration: 'underline', fontWeight: 'bold' }}>
                        upgrade
                    </Link> to a paid plan or come back tomorrow for more!
                </>
            ) : `You have reached your max ${getStoryReloadLimit()} story limit for ${learningLanguage}. Please come back tomorrow for more!`;
        }
    };

    const handleClosePricing = () => {
        setShowPricingOverlay(false);
    };

    const handleLoadButtonClick = async () => {
        if (isTrialExpired()) {
            return;
        }

        if (maxReloadReached) return;
        await fetchStory();
    };

    const hasStory = storyData.story.length > 0;
    const showPasteBox = !hasStory || isPasting;
    const hasPasteText = pasteText.trim().length > 0;

    // The single bottom button doubles as "generate/reload a story" and
    // "process my pasted text", depending on the current mode.
    const handleMainButtonClick = async () => {
        if (showPasteBox && hasPasteText) {
            await handleProcessText();
            return;
        }
        await handleLoadButtonClick();
    };

    const buttonLabel = isProcessingText
        ? "Processing..."
        : (showPasteBox && hasPasteText)
            ? "Process text → story"
            : hasStory
                ? "Press to reload a story..."
                : "Press to load a story...";

    const mainButtonDisabled =
        isProcessingText ||
        loading ||
        ((showPasteBox && hasPasteText)
            // Processing pasted text: only block when access has truly expired.
            ? isTrialExpired()
            // Otherwise the button generates/reloads a story.
            : (maxReloadReached || isTrialExpired()));

    return (
        <div className="stories-container">
            <div className="page-title">Daily {learningLanguage} Story</div>

            {loading || isProcessingText ? (
                <div className="center-text">
                    <div className="loading-text">
                        {isProcessingText ? "Processing your text..." : loadingMessage}
                    </div>
                </div>
            ) : error ? (
                <div className="center-text">
                    <div className="error-text" style={{
                        color: '#c41c1c',
                        fontSize: '1rem',
                        fontWeight: '500'
                    }}>{error}</div>
                </div>
            ) : (
                <>
                    {hasStory && (
                        <div className="stories-mode-bar">
                            <button
                                className="stories-mode-toggle"
                                onClick={() => setIsPasting((prev) => !prev)}
                            >
                                {isPasting ? '← Back to story' : '✎ Paste your own text'}
                            </button>
                        </div>
                    )}

                    {!showPasteBox ? (
                        <>
                            <div className="story-tabs">
                                <div
                                    className={`story-tab ${activeTab === 'original' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('original')}
                                >
                                    {learningLanguage}
                                </div>
                                <div
                                    className={`story-tab ${activeTab === 'translation' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('translation')}
                                >
                                    {baseLanguage}
                                </div>
                            </div>

                            <div className="story-title-header">
                                {activeTab === 'original' ? (
                                    <h3 className="story-title" dangerouslySetInnerHTML={{ __html: storyData.title }} />
                                ) : (
                                    <h3 className="story-title">{storyData.titleTranslation}</h3>
                                )}
                            </div>

                            <div className="story-wrapper">
                                <div className={`story-content ${activeTab === 'original' ? 'active' : ''}`}>
                                    <div className="story-box">
                                        {storyData.story.map((sentence, index) => (
                                            <ClickableSentence
                                                key={index}
                                                sentence={processSentenceJisho(sentence)}
                                                lookupKey={`saved${learningLanguage}Words`}
                                                language={learningLanguage}
                                                baseLanguage={baseLanguage}
                                                lemmatizeSavedWord={true}
                                            />
                                        ))}
                                    </div>
                                </div>

                                <div className={`story-content ${activeTab === 'translation' ? 'active' : ''}`}>
                                    <div className="story-box">
                                        {Array.isArray(storyData.translation)
                                            ? storyData.translation.map((transSentence, index) => (
                                                <p key={index} className="translation-text">
                                                    {processSentenceJisho(transSentence)}
                                                </p>
                                            ))
                                            : typeof storyData.translation === 'string'
                                                ? storyData.translation
                                                    .split('\n')
                                                    .filter(line => line.trim() !== "")
                                                    .map((transSentence, index) => (
                                                        <p key={index} className="translation-text">
                                                            {processSentenceJisho(transSentence)}
                                                        </p>
                                                    ))
                                                : null
                                        }
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="story-wrapper paste-wrapper">
                            <div className="story-box paste-box">
                                <textarea
                                    className="story-paste-textarea"
                                    value={pasteText}
                                    onChange={(e) => setPasteText(e.target.value)}
                                    placeholder={`Paste your own ${learningLanguage} text here — a paragraph, an article, anything you'd like to read with clickable words and ${baseLanguage} translation... Or click the button below to load a story in ${learningLanguage}.`}
                                    spellCheck={false}
                                />
                            </div>
                        </div>
                    )}
                </>
            )}

            {isTrialExpired() ? (
                <div className="max-reload-warning" style={{
                    marginTop: '15px',
                    textAlign: 'center',
                    color: '#c41c1c',
                    fontSize: '1rem',
                    fontWeight: '500'
                }}>
                    <span>{getAccessLimitMessage()}</span>
                </div>
            ) : (
                maxReloadReached && !showPasteBox && (
                    <div className="max-reload-warning" style={{
                        marginTop: '15px',
                        textAlign: 'center',
                        color: '#c41c1c',
                        fontSize: '1rem',
                        fontWeight: '500'
                    }}>
                        {getAccessLimitMessage()}
                    </div>
                )
            )}

            <div className="bottom-bar">
                <div className="story-button-container">
                    <button
                        onClick={handleMainButtonClick}
                        disabled={mainButtonDisabled}
                        className="reload-button"
                    >
                        {loading ? "Loading..." : buttonLabel}
                    </button>
                </div>
            </div>

            <div className="debug-box" style={{
                position: 'fixed',
                top: '70px',
                right: '10px',
                backgroundColor: '#f0f0f0',
                padding: '10px',
                borderRadius: '5px',
                boxShadow: '0 0 5px rgba(0,0,0,0.2)',
                fontSize: '12px',
                zIndex: 1000,
                display: showDebugInfo === 'enabled' ? 'block' : 'none'
            }}>
                <div><strong>Plan:</strong> {userPlan || 'Not available'}</div>
                <div><strong>Max Reloads:</strong> {maxReloadLimit}</div>
                <div><strong>Current Reloads:</strong> {reloadCount || 0}</div>
                <div><strong>Remaining:</strong> {maxReloadLimit - (reloadCount || 0)}</div>
                <button
                    onClick={() => setShowDebugInfo('disabled')}
                    style={{
                        position: 'absolute',
                        top: '-15px',
                        right: '-15px',
                        backgroundColor: '#ff5555',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        fontSize: '10px',
                        cursor: 'pointer'
                    }}
                >
                    X
                </button>
            </div>

            <button
                onClick={() => setShowDebugInfo('enabled')}
                style={{
                    position: 'fixed',
                    top: '70px',
                    right: '10px',
                    backgroundColor: '#555',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    padding: '5px 10px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    zIndex: 999,
                    display: showDebugInfo === 'disabled' ? 'none' : 'block'
                }}
            >
                Debug
            </button>

            {showPricingOverlay && (
                <Pricing onClose={handleClosePricing} />
            )}
        </div>
    );
};

export default Stories;
